import { Editor } from '@tencent/alloy-editor';
import { UndoRedoEventHandler } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-event/undo-redo';
import { CharacterEventHandler } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-event/character';
import { IEditorEventProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/interface/editor-event.interface';
import {
    hasCharacterOfParaElement,
    isCaretAtBeginningOfPara,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/text-util';
import { TabstopEventHandler } from './tabstop';
import { ISelection } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';

type Events = 'tab' | 'enter' | 'backspace' | 'z' | 'y';

type EventHandler = (event: KeyboardEvent) => boolean;

/**
 * 处理editor key事件
 */
export class EditorEvent {
    private readonly eventMap: Record<Events, EventHandler>;

    private editor: Editor;
    private getSelection: (needConvertOffset?: boolean) => ISelection;
    private shouldPreventTab: boolean;
    private undoHandler: UndoRedoEventHandler;
    private characterHandler: CharacterEventHandler;
    private tabstopHandler: TabstopEventHandler;

    public constructor({
        editor,
        getSelection,
        operationHandler,
        triggerUndo,
        triggerRedo,
        shouldPreventTab,
    }: IEditorEventProps) {
        this.editor = editor;
        this.getSelection = getSelection;
        this.shouldPreventTab = shouldPreventTab;
        this.undoHandler = new UndoRedoEventHandler({ triggerUndo, triggerRedo });
        this.characterHandler = new CharacterEventHandler({
            editor,
            getSelection,
            operationHandler,
        });
        this.tabstopHandler = new TabstopEventHandler({ editor });

        this.eventMap = {
            tab: (event: KeyboardEvent): boolean => {
                event.preventDefault?.();
                event.stopPropagation?.();

                if (this.shouldPreventTab) {
                    return false;
                }

                if (this.shouldHandleCharacterTab()) {
                    return this.characterHandler.handleTabEvent(event);
                }
                return this.tabstopHandler.handleTabEvent();
            },
            enter: (event: KeyboardEvent): boolean => this.characterHandler.handleEnterEvent(event),
            backspace: (event: KeyboardEvent): boolean => this.characterHandler.handleBackspaceEvent(event),
            z: (event: KeyboardEvent): boolean => this.undoHandler.handleKeyZEvent(event),
            y: (event: KeyboardEvent): boolean => this.undoHandler.handleKeyYEvent(event),
        };
    }

    public handleEvent(event: KeyboardEvent): boolean {
        const key = event.key?.toLowerCase();
        return this.eventMap[key]?.(event);
    }

    /**
     * 判断是否为项目编号相关的 tab 事件
     */
    private shouldHandleCharacterTab(): boolean {
        const documentValue = this.editor.getValue();
        const selection = this.getSelection();
        if (selection === null) return false;

        const { anchor, focus } = selection;
        const [index] = anchor.path;
        const paragraph = documentValue.children[index];

        if (!hasCharacterOfParaElement(paragraph)) return false;
        if (!isCaretAtBeginningOfPara(anchor, focus)) return false;
        return true;
    }
}
